import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { useFormFactor } from '@/lib/formFactor';
import { useNetwork } from '@/stores/network';
import { useNotificationConnection } from '@/stores/notificationConnection';
import s from './NetworkStatusBar.module.css';

function shownAt(value: number | null) {
  return value ? new Date(value).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }) : '上次';
}

export function NetworkStatusBar() {
  const ff = useFormFactor();
  const offline = useNetwork((state) => state.offline);
  const ordersListErrorPage = useNetwork((state) => state.ordersListErrorPage);
  const lastSuccessAt = useNetwork((state) => state.lastSuccessAt);
  const recoveredUntil = useNetwork((state) => state.recoveredUntil);
  const connected = useNotificationConnection((state) => state.connected);
  const everConnected = useNotificationConnection((state) => state.everConnected);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [writeToast, setWriteToast] = useState(false);
  const recovered = !offline && recoveredUntil > now;
  const streamLost = !offline && !recovered && everConnected && !connected;

  useEffect(() => {
    const onOffline = () => useNetwork.getState().setOffline();
    const onOnline = () => { void api.get('/api/auth/me').catch(() => undefined); };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => { window.removeEventListener('offline', onOffline); window.removeEventListener('online', onOnline); };
  }, []);
  useEffect(() => {
    let timer: number | undefined;
    const show = () => {
      setWriteToast(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setWriteToast(false), 3500);
    };
    window.addEventListener('ims:v2:write-offline', show);
    return () => { window.removeEventListener('ims:v2:write-offline', show); window.clearTimeout(timer); };
  }, []);
  useEffect(() => {
    if (recoveredUntil <= now) return;
    window.dispatchEvent(new Event('ims:v2:refresh'));
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, recoveredUntil - Date.now()));
    return () => window.clearTimeout(timer);
  }, [recoveredUntil, now]);

  const retry = async () => {
    if (streamLost) { window.dispatchEvent(new Event('ims:v2:retry-stream')); return; }
    setBusy(true);
    try { await api.get('/api/auth/me'); window.dispatchEvent(new Event('ims:v2:refresh')); }
    catch { /* request layer preserves offline state */ }
    finally { setBusy(false); }
  };
  if (!offline && !recovered && !streamLost && !writeToast) return null;
  return <>
    {!ordersListErrorPage && (offline || recovered || streamLost) && <div className={`${s.bar} ${ff === 'phone' ? s.phone : ''} ${recovered ? s.recovered : ''}`} role="status">
    <span>{offline ? `网络已断开 · 显示的是 ${shownAt(lastSuccessAt)} 的内容` : recovered ? '已恢复连接，已刷新' : '实时提醒已断开，正在重连'}</span>
    {!recovered && <button type="button" disabled={busy} onClick={() => void retry()}>{busy ? '重试中…' : '重试'}</button>}
    </div>}
    {writeToast && <div className={s.toast} role="alert">没有网络，操作没有提交</div>}
  </>;
}
